import type { IntegrationScope } from "schemas/generated/agentIntegration";
import type { AgentIntegrationUiKey } from "./agentIntegrationDefinitions";

import * as React from "react";

import Nango from "@nangohq/frontend";
import { Alert } from "@replo/design-system/blocks/alert/Alert";
import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { Button } from "@replo/design-system/components/button/Button";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { toast } from "@replo/design-system/components/toast/Toast";
import { skipToken } from "@tanstack/react-query";
import { useIsAgentEnabled } from "@v2/contexts/CurrentProjectContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { agentIntegrationsRouter } from "@v2/trpc/routes/agentIntegrations";
import { routes } from "@v2/utils/router";
import { ArrowLeft } from "lucide-react";
import {
  generatePath,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  getAgentIntegrationByKey,
  getAgentIntegrationIcon,
} from "./agentIntegrationDefinitions";

function getScopeLabel(scope: IntegrationScope): string {
  if (scope.user === true) {
    return "Personal connection";
  }
  if (scope.project === true) {
    return "Project connection";
  }
  if (scope.workspace === true) {
    return "Workspace connection";
  }
  return "Connection";
}

function getScopeDescription(scope: IntegrationScope): string {
  if (scope.user === true) {
    return "This integration is scoped to your account and can be used by Replo Agent when acting on your behalf.";
  }
  if (scope.project === true) {
    return "This integration is scoped to the current project and can be used by Replo Agent in this project.";
  }
  if (scope.workspace === true) {
    return "This integration is scoped to the current workspace and can be used by Replo Agent across projects in your workspace.";
  }
  return "This integration can be used by Replo Agent.";
}

export const AgentIntegrationDetailPage: React.FC = () => {
  const { integrationKey: rawIntegrationKey } = useParams<{
    integrationKey: string;
  }>();
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();
  const agentIntegrationsUtils = agentIntegrationsRouter.useUtils();
  const isReploAgentEnabled = useIsAgentEnabled();
  const [integrationErrorMessage, setIntegrationErrorMessage] = React.useState<
    string | null
  >(null);
  const [isConnectFlowInProgress, setIsConnectFlowInProgress] =
    React.useState(false);

  const integrationDefinition = getAgentIntegrationByKey(
    rawIntegrationKey ?? "",
  );
  const isValidIntegration = Boolean(integrationDefinition);
  const integrationKey: AgentIntegrationUiKey =
    integrationDefinition?.key ?? "google-calendar";

  const { data: connectionStatus } =
    agentIntegrationsRouter.getConnectionStatus.useQuery(
      projectId && isValidIntegration
        ? { integrationKey, projectId }
        : skipToken,
    );

  const { mutateAsync: createConnectSession } =
    agentIntegrationsRouter.createConnectSession.useMutation();
  const { mutateAsync: saveConnection } =
    agentIntegrationsRouter.saveConnection.useMutation();
  const { mutateAsync: deleteConnection, isPending: isDisconnecting } =
    agentIntegrationsRouter.deleteConnection.useMutation();

  const isConnected = connectionStatus?.isConnected ?? false;
  const connectionId = connectionStatus?.connectionId;

  const isPendingAction = isConnectFlowInProgress || isDisconnecting;

  const handleConnect = React.useCallback(async () => {
    if (!projectId || !integrationDefinition) {
      return;
    }

    setIntegrationErrorMessage(null);
    setIsConnectFlowInProgress(true);

    try {
      const { token } = await createConnectSession({
        integrationKey,
        projectId,
      });

      const nangoConnectionId = await new Promise<string | undefined>(
        (resolve, reject) => {
          const nangoClient = new Nango({ connectSessionToken: token });
          const connectUserInterface = nangoClient.openConnectUI({
            onEvent: async (event) => {
              if (event.type === "connect") {
                try {
                  await saveConnection({
                    integrationKey,
                    nangoConnectionId: event.payload.connectionId,
                    projectId,
                  });
                  await agentIntegrationsUtils.getConnectionStatus.invalidate({
                    integrationKey,
                    projectId,
                  });
                  connectUserInterface.close();
                  resolve(event.payload.connectionId);
                } catch (saveConnectionError) {
                  connectUserInterface.close();
                  reject(saveConnectionError);
                }
                return;
              }

              if (event.type === "error") {
                connectUserInterface.close();
                reject(new Error(event.payload.errorMessage));
                return;
              }

              if (event.type === "close") {
                resolve(undefined);
              }
            },
          });
        },
      );
      if (!nangoConnectionId) {
        return;
      }
      toast.success({
        title: "Integration connected",
        description: `${integrationDefinition.title} has been connected successfully.`,
      });
    } catch (connectError) {
      const errorMessage =
        connectError instanceof Error
          ? connectError.message
          : "Failed to connect integration";
      setIntegrationErrorMessage(errorMessage);
      toast.error({ title: "Connection failed", description: errorMessage });
    } finally {
      setIsConnectFlowInProgress(false);
    }
  }, [
    agentIntegrationsUtils.getConnectionStatus.invalidate,
    createConnectSession,
    integrationDefinition,
    integrationKey,
    projectId,
    saveConnection,
  ]);

  const handleDisconnect = React.useCallback(async () => {
    if (!projectId || !connectionId || !integrationDefinition) {
      return;
    }

    setIntegrationErrorMessage(null);
    try {
      await deleteConnection({
        connectionId,
        projectId,
      });
      await agentIntegrationsUtils.getConnectionStatus.invalidate({
        integrationKey,
        projectId,
      });
      toast.success({
        title: "Integration disconnected",
        description: `${integrationDefinition.title} has been disconnected.`,
      });
    } catch (disconnectError) {
      const errorMessage =
        disconnectError instanceof Error
          ? disconnectError.message
          : "Failed to disconnect integration";
      setIntegrationErrorMessage(errorMessage);
      toast.error({
        title: "Disconnection failed",
        description: errorMessage,
      });
    }
  }, [
    agentIntegrationsUtils.getConnectionStatus.invalidate,
    connectionId,
    deleteConnection,
    integrationDefinition,
    integrationKey,
    projectId,
  ]);

  if (!integrationDefinition || !isReploAgentEnabled) {
    return (
      <Navigate
        to={generatePath(routes.shops.integrations.list, {
          projectId: projectId ?? "",
        })}
        replace
      />
    );
  }

  return (
    <div className="flex flex-col w-full h-full max-w-[800px] mx-auto">
      <div className="mb-4">
        <Button
          onClick={() => {
            navigate(
              generatePath(routes.shops.integrations.list, {
                projectId: projectId ?? "",
              }),
            );
          }}
          variant="tertiary"
          size="base"
          startEnhancer={<ArrowLeft />}
          UNSAFE_className="px-0 hover:bg-transparent hover:text-default/60 gap-1"
        >
          Integrations
        </Button>
      </div>

      <PageHeader
        showDivider={false}
        title={
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-lg bg-surface flex items-center justify-center">
              {getAgentIntegrationIcon(integrationKey)}
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="header-h3 text-default">
                  {integrationDefinition.title}
                </span>
                <Tag variant={isConnected ? "success" : "neutral"} size="base">
                  {isConnected ? "Connected" : "Not connected"}
                </Tag>
              </div>
              <span className="text-sm text-muted font-normal leading-relaxed">
                {integrationDefinition.description}
              </span>
            </div>
          </div>
        }
      />

      <div className="flex flex-col gap-3 py-8">
        <div className="flex flex-col rounded-xl border-0.5 border-border shadow-xs">
          <div className="flex flex-col px-5 pt-5 pb-5 border-b-0.5 border-border">
            <span className="text-sm font-medium text-default mb-1">
              {getScopeLabel(integrationDefinition.scope)}
            </span>
            <span className="text-xs text-muted">
              {getScopeDescription(integrationDefinition.scope)}
            </span>
          </div>

          {integrationErrorMessage ? (
            <div className="px-5 pt-4">
              <Alert
                variant="error"
                size="inline"
                title="Action failed"
                description={integrationErrorMessage}
              />
            </div>
          ) : null}

          <div className="flex justify-end px-5 py-5">
            {isConnected ? (
              <Button
                variant="destructive"
                size="base"
                loading={isDisconnecting}
                disabled={isPendingAction}
                onClick={() => {
                  void handleDisconnect();
                }}
              >
                Disconnect
              </Button>
            ) : (
              <Button
                variant="primary"
                size="base"
                loading={isConnectFlowInProgress}
                disabled={isPendingAction}
                onClick={() => {
                  void handleConnect();
                }}
              >
                Connect
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
